<template>
  <div class="matrix-support">
    <lila-message :alert-data="alert" />

    <div class="ms-fields">
      <h2 class="ms-title">
        Solicitar apoio da equipe matricial
      </h2>
      <p class="ms-subtitle">
        A equipe matricial passa a ter acesso a este paciente para discutir o caso com a equipe.
      </p>

      <lila-matrix-support-request
        v-model="request"
        :show-errors="showErrors"
      />

      <div class="ms-actions">
        <lila-button
          type="button"
          title="Enviar pedido"
          @custom-click-event="send"
        />
      </div>
    </div>

    <section class="ms-history">
      <h3 class="ms-history-title">
        Pedidos enviados
      </h3>

      <p
        v-if="patientRequests.length === 0"
        class="ms-empty"
      >
        Nenhum pedido enviado para este paciente.
      </p>

      <ul
        v-else
        class="ms-list"
      >
        <li
          v-for="item in patientRequests"
          :key="item.id"
          class="ms-item"
        >
          <div class="ms-item-head">
            <span class="ms-item-reason">{{ reasonLabel(item.reason) }}</span>
            <span
              class="mr-status"
              :class="statusClass(item.status)"
            >{{ item.status }}</span>
          </div>
          <p class="ms-item-meta">
            {{ formatDate(item.createdAt) }} · {{ modalityLabel(item.modality) }}
          </p>
          <p class="ms-item-question">
            {{ item.question }}
          </p>

          <p
            v-if="item.status === status.discussing"
            class="ms-item-meta ms-item-progress"
          >
            A equipe matricial aceitou o pedido{{ item.acceptedAt ? ` em ${formatDate(item.acceptedAt)}` : "" }} e está analisando o caso.
          </p>

          <div
            v-if="item.response"
            class="ms-response"
          >
            <p class="ms-response-title">
              Parecer da equipe matricial
            </p>
            <p class="ms-item-question">
              {{ item.response.text }}
            </p>
            <p class="ms-item-meta ms-response-meta">
              {{ item.response.author ? `${item.response.author} · ` : "" }}{{ formatDate(item.response.createdAt) }}
            </p>
            <p
              v-if="item.reassessDueDate"
              class="ms-reassess-note"
            >
              Reavaliar até {{ formatDay(item.reassessDueDate) }}. A ação está na aba Ações.
            </p>
          </div>

          <!-- Tela 3: discussão do caso, aberta depois que a matricial aceita -->
          <lila-case-discussion
            v-if="item.status !== status.received"
            :request="item"
            team="assistencial"
            @updated="reloadRequests"
          />
        </li>
      </ul>
    </section>
  </div>
</template>

<script>
// ===== POC Cuidados Paliativos (PNCP) =====
// Aba do paciente para a equipe assistencial (role 1) pedir apoio à equipe
// matricial. Dados da API (/api/palliative/matrix-requests).
import Button from "@components/shared/button/Button.vue";
import Message from "@components/shared/message/Message.vue";
import LilaMatrixSupportRequest from "@components/Chat/LilaMatrixSupportRequest.vue";
import MatrixCaseDiscussion from "@components/Chat/MatrixCaseDiscussion.vue";
import {
  addMatrixRequest,
  listPatientMatrixRequests,
  MATRIX_TEAM,
  statusClass,
  MODALITY_OPTIONS,
  REASON_OPTIONS,
  REQUEST_STATUS
} from "@/services/palliative/matrixSupportApi";
import { showApiError } from "@/services/palliative/http";
import { pocLiveSync } from "@/services/palliative/pocLiveSync";

const emptyRequest = () => ({
  modality: "teleconsultoria",
  reason: "",
  question: ""
});

export default {
  components: {
    "lila-button": Button,
    "lila-message": Message,
    "lila-matrix-support-request": LilaMatrixSupportRequest,
    "lila-case-discussion": MatrixCaseDiscussion
  },
  // Status, mensagens e parecer chegam ao vivo da aba da equipe matricial.
  mixins: [pocLiveSync("reloadRequests")],
  data() {
    return {
      request: emptyRequest(),
      showErrors: false,
      requests: [],
      sending: false,
      status: REQUEST_STATUS,
      alert: {
        message: "",
        type: "",
        show: false
      }
    };
  },
  computed: {
    patientRequests() {
      return this.requests;
    }
  },
  created() {
    this.reloadRequests();
  },
  methods: {
    statusClass,
    async reloadRequests() {
      try {
        this.requests = await listPatientMatrixRequests(this.$route.params.id);
      } catch (error) {
        console.error("POC Cuidados Paliativos: não foi possível carregar os pedidos.", error);
      }
    },
    formatDay(date) {
      return this.$moment(date, "YYYY-MM-DD").format("DD/MM/YYYY");
    },
    reasonLabel(id) {
      const option = REASON_OPTIONS.find((o) => o._id === id);
      return option ? option.name : id;
    },
    modalityLabel(id) {
      const option = MODALITY_OPTIONS.find((o) => o._id === id);
      return option ? option.name : id;
    },
    formatDate(iso) {
      return this.$moment(iso).format("DD/MM/YYYY [às] HH:mm");
    },
    async send() {
      if (!this.request.reason || !this.request.question.trim()) {
        this.showErrors = true;
        return;
      }

      if (this.sending) return;
      this.sending = true;
      try {
        await addMatrixRequest(this.$route.params.id, {
          modality: this.request.modality,
          reason: this.request.reason,
          question: this.request.question.trim()
        });
      } catch (error) {
        showApiError(this, this.alert, error);
        return;
      } finally {
        this.sending = false;
      }

      this.request = emptyRequest();
      this.showErrors = false;
      await this.reloadRequests();

      this.$Utilities.controlAlert(
        null,
        this.alert,
        "success",
        `Pedido de apoio enviado à ${MATRIX_TEAM.name}.`
      );
    }
  }
};
</script>

<style lang="scss" scoped>
@import "./matrixStatus.scss";

$lila-primary: #4f3f82;

.matrix-support {
  width: 100%;
  margin: 0 auto;
  padding: 0 20px 20px 20px;
}

.ms-fields {
  max-width: 560px;
}

.ms-title {
  color: $lila-primary;
  font-weight: 700;
  font-size: 18px;
  margin-bottom: 6px;
}

.ms-subtitle {
  margin-bottom: 20px;
  font-size: 14px;
  color: #6b6b7b;
}

.ms-actions {
  margin-top: 0;
}

.ms-history {
  max-width: 560px;
  margin-top: 32px;
}

.ms-history-title {
  margin-bottom: 12px;
  font-size: 16px;
  font-weight: 700;
  color: $lila-primary;
}

.ms-empty {
  font-size: 14px;
  color: #6b6b7b;
}

.ms-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.ms-item {
  margin-bottom: 12px;
  padding: 14px 16px;
  border-radius: 10px;
  background-color: #ffffff;
  box-shadow: 2px 4px 7px 0 rgba(0, 0, 0, 0.1);
}

.ms-item-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.ms-item-reason {
  font-size: 15px;
  font-weight: 700;
  color: #2b2b3a;
}

.ms-item-progress {
  margin: 10px 0 0;
  color: #8a5a00;
}

.ms-response {
  margin-top: 12px;
  padding: 12px 16px;
  border-left: 3px solid #1e6b3a;
  border-radius: 6px;
  background-color: #f3faf5;
}

.ms-response-title {
  margin-bottom: 4px;
  font-size: 13px;
  font-weight: 700;
  color: #1e6b3a;
}

.ms-response-meta {
  margin: 6px 0 0;
}

.ms-reassess-note {
  margin-top: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #1e6b3a;
}

.ms-item-meta {
  margin: 4px 0 8px;
  font-size: 12px;
  color: #6b6b7b;
}

.ms-item-question {
  font-size: 14px;
  color: #2b2b3a;
  white-space: pre-line;
}
</style>
